import React, { forwardRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { VideoPlayerAdvanced as NYYVideoPlayerAdvanced } from 'yankees-react-utils';
import { Video, HeatMaps, UserReport } from '../data';
import usePlayerSelector from '../utils/usePlayerSelector';

const downloadFile = async ({ tokenType, accessToken }, apiUrl) => {
    const resp = await fetch(apiUrl, {
        headers: { Authorization: `${tokenType} ${accessToken}` },
    });
    const blob = await resp.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = resp.headers.get('content-disposition').split('filename=')[1]; // eslint-disable-line prefer-destructuring

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

const useVideoPlayerFns = ({ heatMapPitcherIdOverride, enableHeatMapFallback }, { PlayerID, FullName }) => {
    const { authState: { accessToken } } = useOktaAuth();

    const grabFrame = async (videoUrl, timestamp) => {
        const queryParams = new URLSearchParams({ videoUrl, timestamp }).toString();
        await downloadFile(accessToken, `/api/video/framegrab?${queryParams}`);
    };
    const download = async (videos) => {
        const queryParams = new URLSearchParams({ videos }).toString();
        await downloadFile(accessToken, `/api/video/download?${queryParams}`);
    };
    const loadPitchData = async ({ gameid, eventseq, pitchseq }) => {
        const resp = await Video.data(accessToken, { gameid, eventseq, pitchseq });
        return (resp && resp.length) ? resp[0] : null;
    };
    const loadPitchHeatmap = async (params) => {
        if (enableHeatMapFallback) {
            const pitchGroups = [[4, 7], [9, 10], [2, 3, 8]];
            const pitchGroup = pitchGroups.find((pg) => pg.includes(params.pitchtype)) || [params.pitchtype];
            const sortedGroup = pitchGroup.sort((a) => (a !== params.pitchtype));
            // eslint-disable-next-line no-restricted-syntax
            for (const p of sortedGroup) {
            // eslint-disable-next-line no-await-in-loop
                const heatMap = await HeatMaps.search(accessToken, {
                    ...params,
                    pitchtype: p,
                    pitcherid: heatMapPitcherIdOverride || params.pitcherid,
                });
                if (heatMap) {
                    return heatMap;
                }
            }
            return null;
        }
        return HeatMaps.search(accessToken, {
            ...params,
            pitcherid: heatMapPitcherIdOverride || params.pitcherid,
        });
    };
    const reportBadVideo = async (subject, description, video, currentClip, errors) => {
        const report = {
            Type: 7,
            Url: window.location.href,
            CurrentClip: currentClip.file,
            UserAgent: window.navigator.userAgent,
            Subject: subject,
            UserComments: `${description}\n\n${JSON.stringify(video, null, 2)}`,
            ConsoleErrors: errors.join('\n\n================================================================================\n\n'),
            LoggedInPlayerId: PlayerID,
            LoggedInPlayerName: FullName,
        };

        const fd = new FormData();
        fd.append('UserReport', JSON.stringify(report));
        await UserReport.post(accessToken, fd);
    };

    return {
        grabFrame,
        download,
        loadPitchData,
        loadPitchHeatmap,
        reportBadVideo,
    };
};

const VideoPlayerAdvanced = forwardRef((props, ref) => {
    const player = usePlayerSelector();
    const { grabFrame, download, loadPitchData, loadPitchHeatmap, reportBadVideo } = useVideoPlayerFns(props, player);

    return (
        <NYYVideoPlayerAdvanced
            grabFrame={grabFrame}
            download={download}
            loadPitchData={loadPitchData}
            loadPitchHeatmap={loadPitchHeatmap}
            reportBadVideo={reportBadVideo}
            ref={ref}
            alwaysShowFilter={false}
            hideFrameGrab
            roundCorners
            {...props}
        />
    );
});

export default VideoPlayerAdvanced;
